import CarouselImagePreviewSquare from '@/components/CarouselImagePreviewSquare';
import leftArrow from '@/icons/left-arrow-icon-filled.svg';
import rightArrow from '@/icons/right-arrow-icon-filled.svg';
import {createShopifyImageUrl} from '@/utilities/create-shopify-image-url';
import {logInfo} from '@/utilities/log';
import {preloadImage} from '@/utilities/preload-image';
import Slider, {CustomArrowProps} from '@ant-design/react-slick';
import classNames from 'classnames';
import {useEffect, useRef, useState} from 'preact/hooks';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

export default ProductImageCarousel;

/**
 * Renders a carousel component for displaying product images.
 *
 * @param customPaging - A custom paging component to display in the carousel. If not provided, the default image preview component is used.
 * @param images - An array of product images.
 * @param featured - The featured image to display when no active image is selected.
 * @param showArrows - Whether to display the arrow buttons for navigating the carousel.
 * @param showPagingOnMobile - Whether to display the paging dots on mobile devices.
 *
 * @example
 * ```tsx
 * <ProductImageCarousel
 *  aspectRatio="8/10"
 *  images={images}
 *  featured={featured}
 *  showArrows={false}
 *  customPaging={({i, image, isActive}) => (
 *   <MyCustomPagingComponent
 *    image={image}
 *    isActive={isActive}
 *    onClick={() => console.log('Clicked on image', i)}
 *  />
 * )}
 * />
 * ```
 */
function ProductImageCarousel({
  aspectRatio = '8/10',
  customPaging,
  images,
  featured,
  showArrows = true,
}: ProductImageCarouselProps) {
  const [active, setActive] = useState(0);
  const mainImage = images[active] ?? featured;

  const slider = useRef<Slider | null>(null);

  useEffect(() => {
    // Force update the carousel when the images change
    // This is a workaround to prevent the carousel from breaking
    // TODO: Investigate a better solution
    slider.current?.forceUpdate();
    slider.current?.slickGoTo(active);

    // Preload each image in large size when the component mounts
    // so they are displayed instantly when the user switches between them
    images.forEach((image) =>
      preloadImage(createShopifyImageUrl(image.url, 640))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images]);

  const thumbnails = useRef<Element[]>([]);

  return (
    <div
      className={classNames('relative', {
        'md:pb-20': images.length <= 1,
      })}
    >
      <Slider
        ref={slider}
        dots={true}
        dotsClass=""
        fade={true}
        easing="ease-in-out"
        infinite={true}
        speed={300}
        arrows={showArrows}
        appendDots={(dots) => (
          <ul
            className={classNames(
              'no-scrollbar flex max-w-full list-none justify-start gap-x-3 justify-self-center overflow-x-scroll'
            )}
          >
            {dots}
          </ul>
        )}
        beforeChange={(_, newIndex) => {
          logInfo('ChangeCarouselImage', {
            index: newIndex,
            url: images[newIndex]?.url,
          });
          setActive(newIndex);

          const activeThumbnail = thumbnails.current[newIndex];
          if (activeThumbnail) {
            activeThumbnail.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'center',
            });
          }
        }}
        // A custom paging component can be injected via props
        // Or the default image preview component is used
        customPaging={(i) => {
          const image = images[i];
          const isActive = i === active;
          return customPaging ? (
            customPaging({i, image, isActive})
          ) : (
            <div>
              <CarouselImagePreviewSquare
                image={image}
                isActive={isActive}
                ref={(el: HTMLButtonElement) => (thumbnails.current[i] = el)}
                onClick={() =>
                  logInfo('ClickCarouselPreviewImage', {
                    index: i,
                    url: image.url,
                  })
                }
              />
            </div>
          );
        }}
        nextArrow={
          <Arrow
            image={{url: rightArrow, alt: 'Right arrow icon'}}
            buttonClassName={classNames(
              'absolute top-1/2 z-10 md:translate-y-[calc(-50%-3rem)] right-3 h-8 w-8 opacity-80 hover:opacity-100 fast-transition translate-y-[calc(-50%-3rem)]'
            )}
          />
        }
        prevArrow={
          <Arrow
            image={{url: leftArrow, alt: 'Left arrow icon'}}
            buttonClassName={classNames(
              'absolute top-1/2 left-3 z-10 md:translate-y-[calc(-50%-3rem)] h-8 w-8 opacity-80 hover:opacity-100 fast-transition translate-y-[calc(-50%-3rem)]'
            )}
          />
        }
      >
        {images.map((image) => (
          <div key={image.url}>
            <img
              src={createShopifyImageUrl(mainImage.url, 640)}
              alt={mainImage.alt ?? ''}
              // eslint-disable-next-line tailwindcss/no-custom-classname
              className="pointer-events-none w-full object-contain"
              style={{aspectRatio}}
            />
          </div>
        ))}
      </Slider>
    </div>
  );
}

/**
 * Renders a custom arrow component with an image.
 * This is used to override the default buttons in the carousel.
 *
 * @param image - The image object containing the URL and alt text.
 * @param style - The inline style for the arrow component.
 * @returns The rendered arrow component.
 */
function Arrow({image, buttonClassName, onClick}: ArrowProps) {
  return (
    <button
      className={buttonClassName}
      onClick={onClick as (_: MouseEvent) => void}
    >
      <img
        src={image.url}
        alt={image.alt ?? ''}
        className="size-full object-contain"
      />
    </button>
  );
}

export type CustomPagingProps = {
  i: number;
  image: Image;
  isActive: boolean;
};

export type ProductImageCarouselProps = {
  customPaging?: (props: CustomPagingProps) => JSX.Element;
  featured: Image;
  images: Image[];
  showArrows?: boolean;
  showPagingOnMobile?: boolean;
  aspectRatio?: string;
};

type ArrowProps = CustomArrowProps & {
  image: Image;
  buttonClassName: string;
};

type Image = {
  alt: string | null;
  url: string;
};
